import '../../assets/css/Dentobook.css';
import '../../assets/css/ExcuseNoteModal.css';
import React, {useContext, useEffect, useState} from "react";
import {Col, DatePicker, Form, Input, Modal, Row, Select} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, CloseOutlined} from "@ant-design/icons";
import {AlertContext} from "../../context/AlertContext";
import dayjs, {Dayjs} from "dayjs";
import {useForm} from "antd/es/form/Form";
import {
    MedicalNoticeIssueResponse,
    MedicalNoticeIssueRequest,
    Patient,
    StatusResponse
} from "../../api-client";
import {getNZIZApi, getMedicalStaffApi, getPatientApi} from "../../api/apiInstances";
import {uuid} from "@ant-design/plots/es/utils";
import {getAdditionalKeyByTownKey} from "../../i18n/health-area";
import {transliterateCyrillicToLatin} from "../../utils/medicalUtils";
import moment from "moment/moment";
import {ALERT_DURATIONS} from "../../constants/alerts";
import logger from "../../utils/logger";
import {
    esignAppEndpoint,
    getQualificationCode,
    getToken, validateNzisMedicalNoticeIssueRequest
} from '../../i18n/nzis-utils';

// Local type for NZIS message header structure
interface Header {
    sender: string;
    senderId: string;
    senderISName: string;
    recipient: string;
    recipientId: string;
    messageId: string;
    messageType: string;
    createdOn: string;
}

// Local type for NZIS medical notice issue contents
interface MedicalNoticeIssueContents {
    medicalNotice: {
        lrn: string;
        basedOn: string | null | undefined;
        authoredOn: string;
        reason: string;
        code: string | undefined;
        fromDate: string;
        toDate: string;
        location: string;
        institution: string;
        purpose: string;
        note: string;
    };
    subject: {
        identifierType: string;
        identifier: string | null | undefined;
        nhifInsuranceNumber: string;
        birthDate: string;
        gender: string;
        name: {
            given: string | null | undefined;
            middle: string | null | undefined;
            family: string | null | undefined;
        };
        address: {
            country: string;
            county: string;
            ekatte: string | null | undefined;
            city: string;
            line: string;
            postalCode: string;
        };
        nationality: string;
        phone: string;
        email: string;
        various: {
            age: string;
        };
    };
    performer: {
        pmi: string;
        qualification: string;
        role: string;
        practiceNumber: string;
        rhifAreaNumber: string;
        nhifNumber: string;
        phone: string;
        email: string;
    };
}

interface ExcuseNotesProps {
    isModalOpen: boolean;
    setIsModalOpen: Function;
    statuses: StatusResponse[];
    patient: Patient;
}

const ExcuseNotes = ({isModalOpen, setIsModalOpen, statuses, patient}: ExcuseNotesProps) => {

    const intl = useIntl();
    const Option = Select.Option;

    const {setError, setVisible, setSuccessMessage} = useContext(AlertContext);
    const [form] = useForm();

    const [loading, setLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<Dayjs | undefined>(dayjs());
    const [endDate, setEndDate] = useState<Dayjs | undefined>(dayjs());
    const [diagnose, setDiagnose] = useState<string>('');
    const [institution, setInstitution] = useState<string>('');

    const nzisApi = getNZIZApi();
    const medicalApi = getMedicalStaffApi();
    const patientApi = getPatientApi();

    useEffect(() => {
        let patientFullName = patient.firstName + ' ' + patient.secondName + ' ' + patient.lastName;
        if (!patient.secondName) {
            patientFullName = patient.firstName + ' ' + patient.lastName;
        }
        form.setFieldValue('patientName', patientFullName);
        form.setFieldValue('startDate', dayjs());
        form.setFieldValue('endDate', dayjs());
        form.setFieldValue('institution', '');
    }, [isModalOpen]);

    const handleServerError = (err: any) => {
        logger.log(err);

        setLoading(false);
        setError(intl.formatMessage({id: 'nzis_data_fail'}));
        setTimeout(() => {
            setVisible(false);
            setError('');
        }, ALERT_DURATIONS.STANDARD);
    };

    const mkbItems = [
        {key: 1, description: "Кариес на зъбите", code: 'K02.1'},
        {key: 2, description: "Пулпит на зъбите", code: 'K04.0'},
        {key: 3, description: "Периодонтит", code: 'K04.5'},
        {key: 4, description: "Фрактура на зъбите", code: 'K08.8'},
        {key: 5, description: "Частична адентия", code: 'K00.0'},
        {key: 6, description: "Тотална адентия", code: 'K00.0'},
        {key: 7, description: "Пародонтит / гингивит / периимплантит", code: 'K05'},
        {key: 8, description: "Състояние след девитализация", code: 'K04.9'},
        {key: 9, description: "Девитализиране по протетична причина", code: 'K04.9'}
    ];

    const handleOk = async () => {
        try {
            await form.validateFields();
            setLoading(true);
            const mkb = mkbItems.find(item => item.key === +diagnose)?.code;
            const toothTreatments = (await patientApi.getPatientToothTreatments(patient.id!)).data;
            const foundToothTreatment = toothTreatments
                .filter(toothTreatment => toothTreatment.nrnDental)
                .sort((toothTreatmentOne, toothTreatmentTwo) => toothTreatmentTwo.createdAt?.localeCompare(toothTreatmentOne.createdAt!)!)[0];

            const today = moment().format('yyyy-MM-DDThh:mm:ss');
            const todayDate = moment().format('yyyy-MM-DD');

            const startDateFormatted = startDate!.format('YYYY-MM-DD');
            const endDateFormatted = endDate!.format('YYYY-MM-DD');

            const patientBirthDate = moment(patient?.birthDate).format('yyyy-MM-DD');
            const patientAge = patient.birthDate ? dayjs(new Date())
                .diff(patient.birthDate!.toString(), 'year').toString() : '0';
            const medicalStaff = (await medicalApi.fetchMedicalStaffById(foundToothTreatment.medicalStaffId!)).data;

            let qualification: string;
            if (medicalStaff.specialisationCode !== undefined) {
                qualification = getQualificationCode(medicalStaff.specialisationCode);
            } else {
                qualification = '';
            }

            const missingFields = validateNzisMedicalNoticeIssueRequest(medicalStaff, patient, foundToothTreatment, intl.locale);
            if (missingFields.length) {
                const fieldsString = missingFields.join(', ');
                const errorMessage = intl.formatMessage({id: 'nzis_mandatory_fields'}).replace('[FIELDS]', fieldsString);
                setLoading(false);
                setError(errorMessage);

                return;
            }

            const header: Header = {
                sender: "2",
                senderId: '010000027',
                senderISName: "Dentobook 1.0",
                recipient: "4",
                recipientId: "NHIS",
                messageId: uuid(),
                messageType: "C041",
                createdOn: today,
            };

            const contents: MedicalNoticeIssueContents = {
                medicalNotice: {
                    lrn: uuid(),
                    basedOn: foundToothTreatment.nrnDental,
                    authoredOn: todayDate,
                    reason: '1',
                    code: mkb,
                    fromDate: startDateFormatted,
                    toDate: endDateFormatted,
                    location: '1',
                    institution: transliterateCyrillicToLatin(institution),
                    purpose: '',
                    note: '',
                },
                subject: {
                    identifierType: '5',
                    identifier: patient.egn,
                    nhifInsuranceNumber: '',
                    birthDate: patientBirthDate,
                    gender: '1',
                    name: {
                        given: patient.firstName,
                        middle: patient.secondName,
                        family: patient.lastName,
                    },
                    address: {
                        country: 'BG',
                        county: getAdditionalKeyByTownKey(patient.healthArea, intl.locale),
                        ekatte: patient.ekatte,
                        city: patient.town ? transliterateCyrillicToLatin(patient.town) : '',
                        line: patient.addressOne ? transliterateCyrillicToLatin(patient.addressOne) : '',
                        postalCode: '',
                    },
                    nationality: '',
                    phone: patient.phone ?? '',
                    email: patient.email ?? '',
                    various: {
                        age: patientAge
                    }
                },
                performer: {
                    pmi: '010000027',
                    qualification: qualification,
                    role: '1',
                    practiceNumber: '1232344545',
                    rhifAreaNumber: '',
                    nhifNumber: '',
                    phone: medicalStaff.phone ?? '',
                    email: medicalStaff.email ?? '',
                },
            };

            const medicalNoticeIssueRequest: MedicalNoticeIssueRequest = {
                header: header,
                contents: contents
            };

            const token = (await getToken(nzisApi)).data.token;

            const responseXml = (await nzisApi.issueNZISmedicalNoticeXml(medicalNoticeIssueRequest)).data;
            const responseSignedXml = await fetch(esignAppEndpoint, {
                method: 'post',
                headers: {
                    'Accept': 'application/xml',
                    'Content-Type': 'application/xml'
                },
                body: responseXml
            });
            const signedXml = await responseSignedXml.text();

            const responseOne = await nzisApi.issueNZISmedicalNotice({token, data: signedXml});
            setLoading(false);
            if (!responseOne.data) {
                await patientApi.createNotice({
                    patient: {id: patient.id},
                    mkb,
                    institution: transliterateCyrillicToLatin(institution),
                    startDate: startDate?.toISOString(),
                    endDate: endDate?.toISOString()
                });
                setLoading(false);
                setIsModalOpen(false);
                setVisible(true);
                setSuccessMessage(intl.formatMessage({id: 'success_message'}));
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            } else {
                setLoading(false);
                setIsModalOpen(false);
                handleServerError({});

                return;
            }
        } catch (err: any) {
            logger.log(err);
            setLoading(false);
            if (!err.errorFields?.length) {
                handleServerError(err);
            }
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={<FormattedMessage id="create_note"/>}
            width={720}
            confirmLoading={loading}
            okText={<FormattedMessage id="confirm"/>}
            cancelText={<FormattedMessage id="cancel"/>}
            closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
            open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
            <Form layout="vertical" form={form}>
                <Form.Item
                    name="patientName"
                    className={"dentobook-form-item dentobook-simple-input mb-32"}
                    initialValue={patient.firstName}
                    label={<FormattedMessage id="name"/>}>
                    <Input
                        disabled
                        bordered={false}
                        value={patient.firstName}/>
                </Form.Item>
                <Row gutter={24}>
                    <Col md={12}>
                        <Form.Item
                            name="startDate"
                            className={"dentobook-form-item"}
                            initialValue={startDate}
                            label={<FormattedMessage id="start_date"/>}
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                            <DatePicker
                                allowClear={false}
                                className="input-fill-width"
                                size={"large"}
                                bordered={true}
                                name={"date"}
                                format={"DD.MM.YYYY"}
                                onChange={e => {
                                    if (e === null) {
                                        setStartDate(undefined);
                                    } else {
                                        setStartDate(e);
                                    }
                                }} placeholder=""/>
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item
                            name="endDate"
                            className={"dentobook-form-item"}
                            initialValue={endDate}
                            label={<FormattedMessage id="end_date"/>}
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                            <DatePicker
                                allowClear={false}
                                className="input-fill-width"
                                size={"large"}
                                bordered={true}
                                name={"date"}
                                format={"DD.MM.YYYY"}
                                onChange={e => {
                                    if (e === null) {
                                        setEndDate(undefined);
                                    } else {
                                        setEndDate(e);
                                    }
                                }} placeholder=""/>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="diagnose"
                           className={"dentobook-form-item dentobook-simple-input input-fill-width"}
                           label={<FormattedMessage id="condition"/>}
                           rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                >
                    <Select
                        placeholder={<FormattedMessage id="selectPlaceholder"/>}
                        size={"large"}
                        value={diagnose}
                        onChange={e => setDiagnose(e)}
                        suffixIcon={<CaretDownOutlined/>}
                        allowClear
                    >
                        {mkbItems.map(item => {
                                return (
                                    <Option key={item.key}
                                            value={item.key}>{item.description}</Option>
                                );
                            })}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="institution"
                    className={"dentobook-form-item dentobook-simple-input mb-32"}
                    initialValue={institution}
                    label={<FormattedMessage id="serve_to"/>}
                    rules={[
                        {
                            required: true,
                            message: <FormattedMessage id="required_field"/>
                        },
                        {
                            pattern: /^[a-zA-Zа-яА-Я\-\s]+$/,
                            message: <FormattedMessage id="letters_only_field"/>
                        }
                    ]}>
                    <Input
                        value={institution}
                        onChange={e => setInstitution(e.target.value)}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ExcuseNotes;